import React from 'react';

import { Text } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';
import { centsToDollarString } from 'utils';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import Currency from 'components/currency';
import Dialog from 'components/dialog';
import { useUIContext } from 'state/ui';

import { ModalBodyContainer, ModalItemContainer, ModalSubtitleContainer } from './styled';
import { IDeliveryFeesTier } from './types';

export const DeliveryFeesStripeModal: React.FC<React.PropsWithChildren<{
  open: boolean;
  onClose: () => void;
  fees: IDeliveryFeesTier;
  smallCartFeeThreshold: number;
}>> = ({
  open,
  onClose,
  fees: { fee, serviceFeePercent, smallCartFee, geographicalFee },
  smallCartFeeThreshold,
}) => {
  const { formatCurrencyForLocale } = useUIContext();
  const { formatMessage } = useIntl();
  if (!open) {
    return null;
  }
  return (
    <Dialog
      showDialog={open}
      testID="delivery-fees-stripe-modal"
      heading={formatMessage({
        id: 'deliveryFees',
      })}
      bodyComponent={
        <ModalBodyContainer>
          <ModalItemContainer testID="delivery-fee-item">
            <ModalSubtitleContainer>
              {formatMessage({
                id: 'deliveryFee',
              })}
              {': '}
              <Currency amount={fee} />
            </ModalSubtitleContainer>
            {formatMessage({ id: 'deliveryFeeDescription' })}
          </ModalItemContainer>
          {geographicalFee > 0 && (
            <ModalItemContainer testID="geographical-fee-item">
              <ModalSubtitleContainer>
                {formatMessage({
                  id: 'geographicalFee',
                })}
                {': '}
                <Currency amount={geographicalFee} />
              </ModalSubtitleContainer>
              {formatMessage(
                {
                  id: 'geographicalFeeDescription',
                },
                {
                  geographicalFeeAmount: centsToDollarString(geographicalFee),
                }
              )}
            </ModalItemContainer>
          )}
          {serviceFeePercent > 0 && (
            <ModalItemContainer testID="service-fee-item">
              <ModalSubtitleContainer>
                {formatMessage({
                  id: 'serviceFee',
                })}
                {': '}
                <Text>{serviceFeePercent}%</Text>
              </ModalSubtitleContainer>
              {formatMessage(
                {
                  id: 'serviceFeeDescription',
                },
                {
                  percent: serviceFeePercent,
                }
              )}
            </ModalItemContainer>
          )}
          {smallCartFee > 0 && (
            <ModalItemContainer testID="small-cart-fee-item">
              <ModalSubtitleContainer>
                {formatMessage({
                  id: 'smallCartFee',
                })}
                {': '}
                <Currency amount={smallCartFee} />
              </ModalSubtitleContainer>
              {smallCartFee > 0 &&
                formatMessage(
                  {
                    id: 'smallCartFeeDescription',
                  },
                  {
                    amount: formatCurrencyForLocale(smallCartFeeThreshold),
                  }
                )}
            </ModalItemContainer>
          )}
        </ModalBodyContainer>
      }
      actions={
        <ActionButton
          onPress={onClose}
          testID="delivery-fees-modal-ok-btn"
          variant={ActionButtonVariants.TEXT_ONLY}
        >
          {formatMessage({
            id: 'okayButtonText',
          })}
        </ActionButton>
      }
      modalAppearanceEventMessage="Delivery Fees Stripe Modal"
      onDismiss={onClose}
    />
  );
};
